<template lang="pug">
  .orderbook
    .liquidity-chart.bids
    .liquidity-chart.quotes
    table.bids
      thead
        th Sum ({{quoteAsset.code}})
        th Size ({{baseAsset.code}})
        th Bid ({{quoteAsset.code}})
      tbody
        tr(
          v-for="order in bids"
          :style="{ 'background': bidBackground(order) }"
        )
          td.sum
            span {{roundNumber(order.amount * order.price)}}
          td.size
            span {{roundNumber(order.amount)}}
          td.price
            span {{prettyPrice(order.price)}}
    table.asks
      thead
        th Ask ({{quoteAsset.code}})
        th Size ({{baseAsset.code}})
        th Sum ({{quoteAsset.code}})
      tbody
        tr(
          v-for="order in asks"
          :style="{ 'background': askBackground(order) }"
        )
          td.price
            span {{prettyPrice(order.price)}}
          td.size
            span {{roundNumber(order.amount)}}
          td.sum
            span {{roundNumber(order.amount * order.price)}}
</template>

<script>
  import { prettyPrice, min, max } from '@/utils'

  import StellarSdk from 'stellar-sdk'
  import { mapState, mapMutations } from 'vuex'

  let stellarServer = new StellarSdk.Server('https://horizon.stellar.org')
  StellarSdk.Network.usePublicNetwork()

  const BID_FILL_BG = 'rgba(0, 255, 0, 0.1)'
  const ASK_FILL_BG = 'rgba(255, 0, 0, 0.075)'

  export default {
    data () {
      return {
        updateOrderbookTimer: null
      }
    },
    computed: {
      ...mapState(['baseAsset', 'quoteAsset', 'asks', 'bids']),
      minBid () { return min(this.bids.map((b) => b.price)) },
      maxBid () { return max(this.bids.map((b) => b.price)) },
      minAsk () { return min(this.asks.map((b) => b.price)) },
      maxAsk () { return max(this.asks.map((b) => b.price)) }
    },
    watch: {
      baseAsset () {
        this.delayedUpdateOrderbook()
      },
      quoteAsset () {
        this.delayedUpdateOrderbook()
      }
    },
    mounted () {
      this.delayedUpdateOrderbook()
    },
    methods: {
      ...mapMutations(['setAsks', 'setBids']),
      roundNumber (num, precision = 8) {
        return parseFloat(num).toFixed(precision)
      },
      prettyPrice (price) {
        return prettyPrice(parseFloat(price))
      },
      delayedUpdateOrderbook () {
        if (this.updateOrderbookTimer) {
          clearTimeout(this.updateOrderbookTimer)
          this.updateOrderbookTimer = null
        }

        this.updateOrderbookTimer = setTimeout(this.updateOrderbook, 100)
      },
      updateOrderbook () {
        stellarServer.orderbook(
          new StellarSdk.Asset(this.baseAsset.code, this.baseAsset.issuer),
          new StellarSdk.Asset(this.quoteAsset.code, this.quoteAsset.issuer)
        ).limit(50).call().then((data) => {
          this.setAsks(data.asks)
          this.setBids(data.bids)
        })
      },
      bidBackground (bid) {
        // let fill = 1 - (minAsk - bid.price) / (minAsk - minBid)
        let fill = (1 - (this.minAsk - this.maxBid) / (this.maxBid - this.minBid)) * (bid.price / this.maxBid)
        if (fill > 0.5) {
          return `linear-gradient(90deg, ${BID_FILL_BG} ${(fill * 100).toFixed(2)}%, transparent ${((1 - fill) * 100).toFixed(2)}%)`
        } else {
          return `linear-gradient(-90deg, transparent ${((1 - fill) * 100).toFixed(2)}%, ${BID_FILL_BG} ${(fill * 100).toFixed(2)}%)`
        }
      },
      askBackground (ask) {
        // let fill = (1 - (ask.price - minBid) / (maxAsk - minBid)) * ((1 - (minAsk - maxBid) / (minAsk - minBid)) / (1 - (minAsk - minBid) / (maxAsk - minBid)))
        let fill = (1 - (this.minAsk - this.maxBid) / (this.maxAsk - this.minAsk)) * (this.minAsk / ask.price)
        if (fill > 0.5) {
          return `linear-gradient(-90deg, ${ASK_FILL_BG} ${(fill * 100).toFixed(2)}%, transparent ${((1 - fill) * 100).toFixed(2)}%)`
        } else {
          return `linear-gradient(90deg, transparent ${((1 - fill) * 100).toFixed(2)}%, ${ASK_FILL_BG} ${(fill * 100).toFixed(2)}%)`
        }
      }
    }
  }
</script>

<style lang="sass" scoped>
  @import "@/assets/styles/variables.sass"

  .orderbook
    display: grid
    grid-template-rows: auto auto
    grid-template-columns: repeat(2, 50%)

  .liquidity-chart
    height: 10rem
    border-top: $border

    &:not(:first-of-type)
      border-left: $border

  table
    font-weight: lighter
    border-collapse: collapse

    th
      padding: 0.75em

      font-weight: lighter
      font-size: 1.25rem
      text-align: right

      background-color: rgba(0, 0, 0, 0)

      border-top: $border
      border-bottom: $border

    tr
      td
        padding: 0.5rem 1rem

        font-size: 1.1rem
        text-align: right

      &:first-of-type
        td
          // padding-top: 1rem

      &:last-of-type
        td
          padding-bottom: 1rem

      &:not(:last-of-type)
        td
          border-bottom: $border

    &:first-of-type
      td, th
        &:last-of-type
          border-right: $border

    &:not(:first-of-type)
      td, th
        &:first-of-type
          padding-left: 0

    .size, .price
      cursor: pointer

    &.asks .price
      color: red

    &.bids .price
      color: green
</style>
